import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import Pagination from '../components/ui/Pagination';
import { Button } from '../components/ui/Button';
import { jobListings, JobListing } from '../data/mockData';
import { BriefcaseIcon, MapPinIcon, ClockIcon, FilterIcon, XIcon, SearchIcon, ChevronDownIcon } from 'lucide-react';
const Jobs = () => {
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 6;
  // State for filters
  const [filters, setFilters] = useState({
    search: '',
    locationType: [] as string[],
    employmentType: [] as string[],
    experienceLevel: [] as string[],
    featured: false
  });
  // State for showing/hiding filters on mobile
  const [showFilters, setShowFilters] = useState(false);
  // Apply filters to job listings
  const filteredJobs = jobListings.filter(job => {
    // Search filter
    if (filters.search && !job.title.toLowerCase().includes(filters.search.toLowerCase()) && !job.company.name.toLowerCase().includes(filters.search.toLowerCase()) && !job.skills.some(skill => skill.toLowerCase().includes(filters.search.toLowerCase()))) {
      return false;
    }
    // Location type filter
    if (filters.locationType.length > 0 && !filters.locationType.includes(job.locationType)) {
      return false;
    }
    // Employment type filter
    if (filters.employmentType.length > 0 && !filters.employmentType.includes(job.employmentType)) {
      return false;
    }
    // Experience level filter
    if (filters.experienceLevel.length > 0 && !filters.experienceLevel.includes(job.experienceLevel)) {
      return false;
    }
    // Featured filter
    if (filters.featured && !job.featured) {
      return false;
    }
    return true;
  });
  // Get current jobs for pagination
  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);
  // Handle filter changes
  const handleFilterChange = (filterType: keyof typeof filters, value: string | boolean) => {
    if (filterType === 'locationType' || filterType === 'employmentType' || filterType === 'experienceLevel') {
      setFilters(prevFilters => {
        const currentValues = prevFilters[filterType] as string[];
        if (currentValues.includes(value as string)) {
          return {
            ...prevFilters,
            [filterType]: currentValues.filter(v => v !== value)
          };
        } else {
          return {
            ...prevFilters,
            [filterType]: [...currentValues, value as string]
          };
        }
      });
    } else {
      setFilters(prevFilters => ({
        ...prevFilters,
        [filterType]: value
      }));
    }
  };
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      locationType: [],
      employmentType: [],
      experienceLevel: [],
      featured: false
    });
  };
  // Format date to relative time (e.g., "2 days ago")
  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else {
      const months = Math.floor(diffDays / 30);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
  };
  // Format salary range
  const formatSalary = (job: JobListing) => {
    const {
      min,
      max,
      currency
    } = job.salaryRange;
    // Check if it's hourly rate or annual salary
    if (min < 1000) {
      return `${currency}${min}-${max}/hr`;
    }
    // Format annual salary with K or M
    const formatNumber = (num: number) => {
      if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M`;
      }
      return `${Math.round(num / 1000)}K`;
    };
    return `${currency}${formatNumber(min)}-${formatNumber(max)}`;
  };
  return <div className="bg-white min-h-screen" data-id="element-917">
      {/* Hero Section */}
      <section className="bg-[#FFF5F5] py-16 md:py-20" data-id="element-918">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-919">
          <div className="max-w-3xl mx-auto text-center" data-id="element-920">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-921">
              Find Your Next Laravel Job
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-922">
              Browse through our curated list of job opportunities for Laravel
              developers
            </p>
            <div className="relative max-w-2xl mx-auto" data-id="element-923">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-924">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-925" />
              </div>
              <input type="text" className="block w-full pl-10 pr-20 py-3 border border-[#FF3B30] rounded-md shadow-md focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:border-[#FF3B30]" placeholder="Search for jobs, companies, or skills..." value={filters.search} onChange={e => handleFilterChange('search', e.target.value)} data-id="element-926" />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2" data-id="element-927">
                <Button variant="primary" size="sm" className="mr-1" onClick={() => {}} data-id="element-928">
                  Search
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-929">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-930">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8" data-id="element-931">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-6" data-id="element-932">
              <Button variant="secondary" fullWidth className="flex items-center justify-center" onClick={() => setShowFilters(!showFilters)} data-id="element-933">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-934" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>
            {/* Filters Sidebar */}
            <div className={`${showFilters ? 'block' : 'hidden'} lg:block lg:col-span-1 space-y-6`} data-id="element-935">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-936">
                <div className="flex items-center justify-between mb-5" data-id="element-937">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-938">
                    Filters
                  </h3>
                  <Button variant="ghost" size="sm" className="text-gray-500 hover:text-[#FF3B30]" onClick={clearFilters} data-id="element-939">
                    Clear All
                  </Button>
                </div>
                {/* Location Type Filter */}
                <div className="mb-6" data-id="element-940">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-941">
                    Location Type
                  </h4>
                  <div className="space-y-2.5" data-id="element-942">
                    {['remote', 'onsite', 'hybrid'].map(type => <label key={type} className="flex items-center cursor-pointer group" data-id="element-943">
                        <input type="checkbox" className="h-4 w-4 text-[#FF3B30] focus:ring-[#FF3B30] border-[#FF3B30] rounded shadow-sm" checked={filters.locationType.includes(type)} onChange={() => handleFilterChange('locationType', type)} data-id="element-944" />
                        <span className="ml-2.5 text-sm text-gray-700 capitalize group-hover:text-[#FF3B30] transition-colors" data-id="element-945">
                          {type}
                        </span>
                      </label>)}
                  </div>
                </div>
                {/* Employment Type Filter */}
                <div className="mb-6" data-id="element-946">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-947">
                    Employment Type
                  </h4>
                  <div className="space-y-2.5" data-id="element-948">
                    {['full-time', 'part-time', 'contract', 'freelance'].map(type => <label key={type} className="flex items-center cursor-pointer group" data-id="element-949">
                          <input type="checkbox" className="h-4 w-4 text-[#FF3B30] focus:ring-[#FF3B30] border-[#FF3B30] rounded shadow-sm" checked={filters.employmentType.includes(type)} onChange={() => handleFilterChange('employmentType', type)} data-id="element-950" />
                          <span className="ml-2.5 text-sm text-gray-700 capitalize group-hover:text-[#FF3B30] transition-colors" data-id="element-951">
                            {type.replace('-', ' ')}
                          </span>
                        </label>)}
                  </div>
                </div>
                {/* Experience Level Filter */}
                <div className="mb-6" data-id="element-952">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-953">
                    Experience Level
                  </h4>
                  <div className="space-y-2.5" data-id="element-954">
                    {['entry', 'mid', 'senior', 'lead'].map(level => <label key={level} className="flex items-center cursor-pointer group" data-id="element-955">
                        <input type="checkbox" className="h-4 w-4 text-[#FF3B30] focus:ring-[#FF3B30] border-[#FF3B30] rounded shadow-sm" checked={filters.experienceLevel.includes(level)} onChange={() => handleFilterChange('experienceLevel', level)} data-id="element-956" />
                        <span className="ml-2.5 text-sm text-gray-700 group-hover:text-[#FF3B30] transition-colors" data-id="element-957">
                          {level === 'entry' ? 'Entry Level' : level === 'mid' ? 'Mid Level' : level === 'senior' ? 'Senior Level' : 'Lead / Manager'}
                        </span>
                      </label>)}
                  </div>
                </div>
                {/* Featured Jobs Filter */}
                <div data-id="element-958">
                  <label className="flex items-center cursor-pointer group" data-id="element-959">
                    <input type="checkbox" className="h-4 w-4 text-[#FF3B30] focus:ring-[#FF3B30] border-[#FF3B30] rounded shadow-sm" checked={filters.featured} onChange={() => handleFilterChange('featured', !filters.featured)} data-id="element-960" />
                    <span className="ml-2.5 text-sm text-gray-700 group-hover:text-[#FF3B30] transition-colors" data-id="element-961">
                      Featured Jobs Only
                    </span>
                  </label>
                </div>
              </div>
              {/* Post a Job CTA */}
              <div className="bg-[#FFEDEC] p-6 rounded-lg border border-[#FF3B30] text-center" data-id="element-962">
                <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-963">
                  Hiring Laravel Developers?
                </h3>
                <p className="text-sm text-gray-700 mb-5" data-id="element-964">
                  Post a job listing and reach thousands of certified Laravel
                  developers.
                </p>
                <Button variant="primary" to="/post-job" data-id="element-965">
                  Post a Job
                </Button>
              </div>
            </div>
            {/* Job Listings */}
            <div className="lg:col-span-3 mt-8 lg:mt-0" data-id="element-966">
              {/* Results summary */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6" data-id="element-967">
                <div className="mb-4 sm:mb-0" data-id="element-968">
                  <p className="text-sm text-gray-600" data-id="element-969">
                    Showing{' '}
                    <span className="font-medium" data-id="element-970">
                      {filteredJobs.length > 0 ? indexOfFirstJob + 1 : 0}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium" data-id="element-971">
                      {Math.min(indexOfLastJob, filteredJobs.length)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium" data-id="element-972">{filteredJobs.length}</span>{' '}
                    jobs
                  </p>
                </div>
                <div className="flex items-center" data-id="element-973">
                  <span className="text-sm text-gray-700 mr-2" data-id="element-974">Sort by:</span>
                  <div className="relative" data-id="element-975">
                    <select className="appearance-none bg-white border border-[#FF3B30] rounded-md pl-3 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:border-[#FF3B30] shadow-md" defaultValue="recent" data-id="element-976">
                      <option value="recent" data-id="element-977">Most Recent</option>
                      <option value="salary-high" data-id="element-978">Highest Salary</option>
                      <option value="salary-low" data-id="element-979">Lowest Salary</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700" data-id="element-980">
                      <ChevronDownIcon className="h-4 w-4" data-id="element-981" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Job Cards */}
              {currentJobs.length > 0 ? <div className="space-y-6" data-id="element-982">
                  {currentJobs.map(job => <div key={job.id} className={`bg-white rounded-lg border ${job.featured ? 'border-[#FF3B30] shadow-md' : 'border-gray-200 shadow-sm'} overflow-hidden hover:shadow-lg transition-shadow duration-300`} data-id="element-983">
                      {job.featured && <div className="bg-[#FF3B30] text-white text-xs font-semibold px-3 py-1" data-id="element-984">
                          Featured
                        </div>}
                      <div className="p-6" data-id="element-985">
                        <div className="sm:flex items-start" data-id="element-986">
                          <div className="mb-4 sm:mb-0 sm:mr-5 flex-shrink-0" data-id="element-987">
                            <div className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden" data-id="element-988">
                              <img src={job.company.logo} alt={`${job.company.name} logo`} className="w-full h-full object-contain p-2" data-id="element-989" />
                            </div>
                          </div>
                          <div className="flex-grow" data-id="element-990">
                            <div className="flex flex-col sm:flex-row sm:justify-between" data-id="element-991">
                              <div data-id="element-992">
                                <h3 className="text-xl font-semibold text-gray-900 mb-1 hover:text-[#FF3B30] transition-colors" data-id="element-993">
                                  {job.title}
                                </h3>
                                <p className="text-sm text-gray-600 mb-2" data-id="element-994">
                                  {job.company.name}
                                </p>
                              </div>
                              <div className="mt-2 sm:mt-0 text-sm font-medium text-gray-700" data-id="element-995">
                                {formatSalary(job)}
                              </div>
                            </div>
                            <div className="mt-4 flex flex-wrap items-center text-sm text-gray-600 gap-y-2" data-id="element-996">
                              <div className="flex items-center mr-4 mb-0" data-id="element-997">
                                <MapPinIcon className="h-4 w-4 mr-1.5 text-gray-400" data-id="element-998" />
                                {job.location}
                                <span className="ml-1.5 px-2 py-0.5 bg-gray-100 rounded-full text-xs capitalize" data-id="element-999">
                                  {job.locationType}
                                </span>
                              </div>
                              <div className="flex items-center mr-4 mb-0" data-id="element-1000">
                                <BriefcaseIcon className="h-4 w-4 mr-1.5 text-gray-400" data-id="element-1001" />
                                <span className="capitalize" data-id="element-1002">
                                  {job.employmentType.replace('-', ' ')}
                                </span>
                              </div>
                              <div className="flex items-center mb-0" data-id="element-1003">
                                <ClockIcon className="h-4 w-4 mr-1.5 text-gray-400" data-id="element-1004" />
                                {formatRelativeDate(job.postedDate)}
                              </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2" data-id="element-1005">
                              {job.skills.slice(0, 5).map(skill => <span key={skill} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700" data-id="element-1006">
                                  {skill}
                                </span>)}
                              {job.skills.length > 5 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-1007">
                                  +{job.skills.length - 5} more
                                </span>}
                            </div>
                            <div className="mt-6 flex flex-col sm:flex-row sm:justify-between sm:items-center" data-id="element-1008">
                              <p className="text-sm text-gray-600 mb-4 sm:mb-0 line-clamp-2 sm:max-w-md" data-id="element-1009">
                                {job.description.substring(0, 150)}
                                {job.description.length > 150 ? '...' : ''}
                              </p>
                              <div className="flex-shrink-0" data-id="element-1010">
                                <Button variant="primary" href={job.applicationUrl} data-id="element-1011">
                                  Apply Now
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="bg-white p-8 text-center rounded-lg border border-gray-200 shadow-sm" data-id="element-1012">
                  <div className="flex justify-center mb-4" data-id="element-1013">
                    <BriefcaseIcon className="h-12 w-12 text-gray-400" data-id="element-1014" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1015">
                    No jobs found
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto" data-id="element-1016">
                    We couldn't find any jobs matching your current filters. Try
                    adjusting your search criteria.
                  </p>
                  <Button variant="primary" onClick={clearFilters} data-id="element-1017">
                    Clear Filters
                  </Button>
                </div>}
              {/* Pagination */}
              {filteredJobs.length > jobsPerPage && <div className="mt-10 flex justify-center" data-id="element-1018">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-1019" />
                </div>}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="bg-[#FFF5F5] py-16 md:py-20" data-id="element-1020">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1021">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1022">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1023">
              Need help with your job search?
            </h2>
            <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto" data-id="element-1024">
              Create a developer profile to get discovered by top companies or
              browse our resources to improve your skills.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1025">
              <Button variant="primary" size="lg" to="/signup" data-id="element-1026">
                Create Developer Profile
              </Button>
              <Button variant="outline" size="lg" to="/developers" data-id="element-1027">
                Browse Developer Resources
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Jobs;